/**
 * Single source of truth for the How it works page content.
 * Consumed by the page itself, its FAQ/HowTo JSON-LD and /llms.txt so the
 * human page, structured data and AI crawlers never drift apart.
 */

export const HOW_IT_WORKS_INTRO =
  "BeHot is where anything can compete for attention. List a song, gig, profile, brand, startup, app, product, business, event or even just an idea, boost it, and climb the charts: LIST IT → BOOST IT → CLIMB → GET SEEN. Positions are earned with Heat, not algorithms — transparent, live, and reversible the second someone outbids you.";

export const STEPS: { title: string; body: string }[] = [
  {
    title: "1. Get on a board",
    body: "Submit your listing to any category. We auto-fill your name, logo, description and socials from your link, then a human approves it. Once approved you appear in Discover, ready to compete.",
  },
  {
    title: "2. Pay the entry cost",
    body: "Entering a board costs the lowest active Heat on that board plus the board's increment. If the cheapest active listing has 12 Heat and the increment is 1, your first boost must be at least 13 Heat. This keeps the race fair and the chart always moving upward.",
  },
  {
    title: "3. Add Heat",
    body: "A Boost adds Heat to your listing on that board. Heat is simply the total value of your currently active boosts there, nothing hidden, nothing weighted.",
  },
  {
    title: "4. Take a position",
    body: "The highest active Heat holds position 01. Ties break by who reached that level first, then by who published first. Positions recalculate the moment a payment clears.",
  },
  {
    title: "5. Heat cools",
    body: "Every boost runs for 28 days. When it expires the Heat leaves the board, positions shift, and anyone below you moves up. Boards also reset every 30 days for a new season.",
  },
];

export const RANKING_RULES: string[] = [
  "Rankings are paid placement and labelled as such on every board.",
  "Active Heat = sum of your live boosts on that board. Expired boosts count for nothing.",
  "Entering a board costs the lowest active Heat on that board plus the board's increment. Example: if the last listed item has 12 Heat and the increment is 1, you need at least 13 Heat to enter.",
  "Every boost lasts 28 days, and every board starts a fresh season every 30 days where entry opens again from 3 Heat.",
  "Ties break by who reached the level first, then by earliest publication.",
  "Boosts are one-shot purchases priced at US$1.00 per Heat. No hidden renewals.",
  "Listings must be accurate. Misleading listings are removed and Heat is forfeited.",
];

export const GLOSSARY: [string, string][] = [
  ["Board", "A category or sub-category leaderboard, e.g. AI Products or DJs. Parent boards aggregate the Heat of their sub-boards."],
  ["Heat", "The unit of ranking. 1 Heat = US$1.00 of boost value. Your position is the sum of your active Heat on a board."],
  ["Boost", "A one-off purchase that adds Heat for 28 days. No renewals."],
  ["Minimum boost", "The lowest amount accepted on a board for a first boost. On an empty board this is the opening offer (usually 3 Heat). Once listings are active, it becomes the lowest active Heat plus the increment."],
  ["Minimum increment", "The smallest step above the lowest active Heat needed to enter a board. If the cheapest active listing has 12 Heat and the increment is 1, the next entry must be at least 13 Heat."],
  ["Entry cost", "What you actually pay to land on a board for the first time: lowest active Heat + increment. Shown live on every board and boost page."],
  ["Position 01", "The top slot on a board, the highest active Heat holds it."],
];

export const FAQ: [string, string][] = [
  ["Is this paid placement?", "Yes, and we label it everywhere. Positions are bought with Heat, not decided by an opaque algorithm. That's the whole point."],
  ["What does a Boost cost?", "Each board sets its own minimum boost and minimum increment. The exact amount needed to overtake position 01 is shown live before you pay."],
  ["How much does it cost to enter a board?", "Entering a board costs the lowest active Heat on that board plus the board's increment. If the cheapest active listing has 12 Heat and the increment is 1, you need at least 13 Heat to enter. On a fresh or empty board the entry cost is the opening offer, usually 3 Heat."],
  ["What is the increment?", "The increment is the minimum step above the current lowest active Heat required for a new listing to join the board. It keeps the chart competitive and prevents ties. Most boards use an increment of 1 Heat."],
  ["Do boosts renew?", "Never. A Boost is a one-shot purchase that lasts 28 days. When it ends, the Heat expires and you decide whether to buy again."],
  ["Can I be refunded?", "Boosts are non-refundable once live, since the placement is delivered immediately. If a payment fails or a listing is removed by moderation before going live, you're refunded in full."],
  ["Who can submit a listing?", "Anyone with an account. Every submission goes through moderation. Misleading, illegal or impersonating listings are rejected or removed."],
  ["Do I need to own the thing I list?", "No, you can boost anything you want to support. Owners can claim their listing later to edit it."],
  ["What currency is used?", "Prices on the site are shown in USD, 1 Heat = US$1.00. Card payments are handled securely by Stripe and your receipt shows the exact amount charged."],
  ["Where are my invoices?", "In your account under Invoices. Add company name and tax ID under Billing and they'll appear on every invoice, and each paid order is emailed to you as a PDF."],
  ["How do I top up my wallet?", "Go to Wallets in your account and top up by card from US$5. Funds land in your Top-up wallet and can be spent on listings, boosts and services."],
  ["What are the three wallets for?", "Top-up pays for platform services. Earn holds creator income and is the only withdrawable wallet. Referral holds Reward Heat: internal credit you can spend on boosts and on Marketplace orders, but not withdraw."],
  ["How do payouts work?", "Withdraw from the Earn wallet from US$100, by bank transfer in EUR, USD or AED, or to a USDT/USDC ERC-20 wallet. Requests are processed after verification within 48 hours and can take 5 to 8 working days."],
  ["What does the platform take?", "A 15% fee on completed creator earnings, deducted before the amount is credited to your Earn wallet. Crowdfunding on Fund will carry a 4.85% fee."],
  ["How does the referral programme work?", "You earn 2 Heat when someone signs up with your link and 10 Heat each time a referred member spends US$100. Reward Heat is internal credit and can be spent on boosts and on Marketplace orders."],
  ["How long does approval take?", "Most listings are reviewed within minutes. If moderation hasn't acted within 20 minutes, the listing is approved automatically."],
  ["Do the charts reset?", "Yes — boards are refreshed at the start of each month, so every season starts with a fresh race for position 01."],
  ["How do I get support?", "Use the contact form on this page and choose Request support, or Contact sales for campaigns, volume boosts and partnerships."],
];

export type ServiceIcon =
  | "boards"
  | "categories"
  | "listings"
  | "boosts"
  | "wallets"
  | "billing"
  | "payouts"
  | "referrals"
  | "profiles"
  | "promote"
  | "earn"
  | "marketplace"
  | "fund";

export const SERVICES: {
  key: ServiceIcon;
  title: string;
  body: string;
  to?: string;
  soon?: boolean;
}[] = [
  {
    key: "boards",
    title: "Boards",
    body: "Live paid charts for creators, artists, startups, apps and brands. Every category has its own board, its own minimum boost, 28-day boosts and a fresh season every 30 days. Parent boards aggregate the Heat of their sub-boards.",
    to: "/hot",
  },
  {
    key: "categories",
    title: "Categories",
    body: "Browse every board and sub-board, see how much Heat each one holds, and find where your listing belongs before you submit it.",
    to: "/categories",
  },
  {
    key: "listings",
    title: "Get Hot (listings)",
    body: "Submit a link and we auto-fill the name, logo, description and socials from the page. A listing goes live with its first paid boost, and is auto-approved if moderation has not reviewed it within 20 minutes.",
    to: "/get-hot",
  },
  {
    key: "boosts",
    title: "Boosts",
    body: "Owners boost by paying the position price to take a slot. Fans can back any listing with a free bid from 3 Heat upwards. Boosts never renew — they last 28 days, then the Heat leaves the board.",
  },
  {
    key: "wallets",
    title: "Wallets",
    body: "Three wallets: Top-up (pays for services), Earn (creator income, withdrawable) and Referral (Reward Heat, spendable on boosts and Marketplace orders). Top up from US$5 by card.",
    to: "/app/wallets",

  },
  {
    key: "billing",
    title: "Billing & invoices",
    body: "Prices are shown in USD; charging and invoicing happen in AED. Every paid order produces a sequential PDF invoice, emailed to you and stored in your account with your company name and tax ID.",
    to: "/app/invoices",
  },
  {
    key: "payouts",
    title: "Payouts & withdrawals",
    body: "Creators withdraw from the Earn wallet from US$100 by bank transfer (EUR, USD or AED) or USDT/USDC on ERC-20. Payout details are protected by email OTP. Requests are verified within 48h and paid within 5 to 8 working days.",
    to: "/app/wallets",
  },
  {
    key: "referrals",
    title: "Referrals",
    body: "Share your link and earn 2 Heat when someone signs up, plus 10 Heat every time a person you referred spends US$100. Reward Heat boosts your listings or anyone else's.",
    to: "/referrals",
  },
  {
    key: "profiles",
    title: "Public profiles",
    body: "Your profile at /user/your-slug (or /pro/your-slug for professionals) is public, SEO-indexed and social-preview ready: bio, connected socials, showcase posts, services and share buttons with your referral link built in.",
    to: "/app/profile",
  },
  {
    key: "promote",
    title: "Promote",
    body: "Buy real promotional actions: creator posts and stories, playlist placement and curator submissions for music, plus simplified Meta and Zeely-style ad campaigns for brands — fixed prices, escrow and proof of delivery.",
    to: "/promote",
    soon: true,
  },
  {
    key: "earn",
    title: "Earn",
    body: "Creators publish a rate card, get booked, deliver with proof and withdraw in USD. A 15% platform fee is deducted before the amount lands in your Earn wallet.",
    to: "/earn",
    soon: true,
  },
  {
    key: "marketplace",
    title: "Marketplace",
    body: "Brands post paid offers, creators apply. Professionals pay 10 Heat to publish an offer; browsing is reserved for signed-in members.",
    to: "/marketplace",
    soon: true,
  },
  {
    key: "fund",
    title: "Fund",
    body: "Crowdfunding for projects: present your idea, set a goal and let the community back it. Platform fee 4.85% on funds raised.",
    to: "/fund",
    soon: true,
  },
];
